import { useMutation, useQuery } from '@tanstack/react-query';
import { Building2Icon, ChevronRightIcon, FileIcon, Loader2, Upload, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type DataUpload, DataUploadType } from '@/common/data-upload';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog';
import { useFileUploadActivity } from '@/context/FileUploadActivityContext';
import { useUploadBusy } from '@/hooks/use-upload-busy';
import { cn } from '@/lib/utils';
import { apiErrorMessage } from '@/queries/common';
import { addDataUpload } from '@/queries/data-uploads/add-data-upload';
import { queryClient } from '@/queries/query.client';
import { getListSubAssetsOptions } from '@/queries/sub-assets/list-sub-assets';
import { PRESIGNED_UPLOAD_SCOPE, uploadFileWithPresignedUrl } from '@/queries/uploads/presigned-upload';
import { ROLE } from '../../../common/role';
import type { SubAsset } from '../../../common/sub-asset';
import { useAppContext } from '../../../context/AppContext';
import { usePermission } from '../../../hooks/use-permission';
import { buildSubAssetPath, getTreeView } from '../../../lib/utils/asset';
import { getAssetOptions } from '../../../queries/assets/get-asset';
import { SubAssetTreeSelector } from '../../sub-asset/sub-asset-selector/SubAssetTreeSelector';
import { Label } from '../../ui/label';

export type UploadFilesProps = {
	onAdd?: () => void;
	assetId?: string;
	inspectionId?: string;
	damageId?: string;
};

// Determine the presigned upload scope based on which IDs are present in props
const getPresignedScopeFromProps = (props: UploadFilesProps): PRESIGNED_UPLOAD_SCOPE => {
	if (props.damageId) return PRESIGNED_UPLOAD_SCOPE.DAMAGE;
	if (props.inspectionId) return PRESIGNED_UPLOAD_SCOPE.INSPECTION;
	return PRESIGNED_UPLOAD_SCOPE.ASSET;
};

export function UploadFiles({ onAdd, assetId, damageId, inspectionId }: UploadFilesProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const { selectedAssetId: assetIdFromContext } = useAppContext();
	const [isAssetTreeOpen, setIsAssetTreeOpen] = useState(false);
	const [selectedAssetStructureId, setSelectedAssetStructureId] = useState<string>('');

	const [files, setFiles] = useState<File[]>([]);
	const [isUploading, setIsUploading] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const shouldUploadFiles = usePermission(ROLE.FILES_UPLOAD);
	const { beginFileUpload, endFileUpload } = useFileUploadActivity();
	// A transfer already running (plain file or model) blocks starting another one.
	const isUploadBusy = useUploadBusy();

	useEffect(() => {
		if (!open) {
			setFiles([]);
			setIsUploading(false);
		}
	}, [open]);

	useEffect(() => {
		if (open && assetIdFromContext) {
			setSelectedAssetStructureId(assetIdFromContext);
		}
	}, [open, assetIdFromContext]);

	const handleUploadClick = (e?: React.MouseEvent) => {
		if (!shouldUploadFiles) {
			// The button is wrapped in a DialogTrigger, which opens the dialog on
			// click independently of this handler unless the event is prevented.
			e?.preventDefault();
			toast.error(t('files.upload.noPermission'));
			return;
		}
		fileInputRef.current?.click();
	};

	// Handle file selection (Append to existing list)
	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			const newFiles = Array.from(e.target.files);

			setFiles((prev) => [...prev, ...newFiles]);
		}
		// Reset value so the same file can be selected again if needed
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
		setOpen(true);
	};

	// Remove a specific file from the queue
	const removeFile = (indexToRemove: number) => {
		setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
	};
	const addDataUploadsApi = useMutation({
		mutationFn: async (data: Partial<DataUpload>) => addDataUpload(data),
	});

	const { data: subAssetsResponse } = useQuery(getListSubAssetsOptions(assetId || '', { limit: 300, offset: 0 }));
	const { data: asset } = useQuery(getAssetOptions(assetId || ''));

	const subAssetsWithFirstParent = useMemo((): SubAsset[] => {
		if (!subAssetsResponse?.subAssets || !asset?.id) return [];
		const children = getTreeView(subAssetsResponse?.subAssets);
		return [
			{
				id: asset?.id as string,
				name: asset?.name || '',
				parentSubAssetId: null,
				children,
			},
		];
	}, [subAssetsResponse?.subAssets, asset?.id, asset?.name]);

	const onUploadConfirmClick = async (e: React.FormEvent) => {
		e.preventDefault();
		if (files.length === 0) {
			toast.error(t('files.upload.noFilesSelected'));
			return;
		}
		// Use assetId from props or fallback to context if not provided
		if (!assetId) {
			toast.error(t('files.upload.assetIdRequired'));
			return;
		}

		setIsUploading(true);
		// Published globally as well: the dialog can be dismissed mid-transfer, so
		// the other upload entry points need a flag that outlives it.
		beginFileUpload();

		try {
			await Promise.all(
				files.map(async (file) => {
					// Determine scope from props
					const scope = getPresignedScopeFromProps({ assetId, damageId, inspectionId });

					// Get presigned URL and upload directly to S3
					const { key } = await uploadFileWithPresignedUrl(file, {
						scope,
						assetId,
						inspectionId,
						damageId,
					});

					// Record the upload in the backend — store the S3 key as fileUrl.
					// Read access is via GET /data-uploads/:id/presigned-url.
					await addDataUploadsApi.mutateAsync({
						name: file.name,
						assetId,
						fileType: file.type,
						fileSizeInKB: (file.size || 0) / 1024,
						fileUrl: key,
						uploadType: DataUploadType.FILE,
						subAssetId:
							selectedAssetStructureId && selectedAssetStructureId !== assetId ? selectedAssetStructureId : undefined,
						inspectionId: inspectionId ? inspectionId : undefined,
						damageId: damageId ? damageId : undefined,
					});
				}),
			);

			toast.success(t('files.upload.uploadSuccess', { count: files.length }));
			await queryClient.invalidateQueries({ queryKey: ['data-uploads', 'list'] });
			onAdd?.();
			setOpen(false);
		} catch (error) {
			console.error('File upload error:', error);
			toast.error(t('files.upload.uploadFilesFailed'), { description: apiErrorMessage(error) });
		} finally {
			setIsUploading(false);
			endFileUpload();
		}
	};

	const getAssetBreadcrumbPath = (assetId: string): string => {
		const path = buildSubAssetPath(subAssetsWithFirstParent, assetId);
		return path.map((s) => s.name || '').join(' > ');
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button
					size="sm"
					className="h-7 lg:h-9 text-xs lg:text-sm px-2 lg:px-3"
					disabled={isUploadBusy}
					title={isUploadBusy ? t('files.upload.uploadInProgressHint') : undefined}
					onClick={(e) => {
						handleUploadClick(e);
					}}
				>
					<Upload className="size-3.5 lg:size-4 mr-1" />
					{t('files.upload.uploadFilesButton')}
				</Button>
			</DialogTrigger>
			{/* Hidden Input for handling multiple file selection */}
			<input type="file" multiple className="hidden" ref={fileInputRef} onChange={handleFileChange} />

			<DialogContent className="max-w-md w-full">
				<DialogHeader className="flex flex-col space-y-1.5 text-center sm:text-left">
					<DialogTitle className="text-lg font-semibold leading-none tracking-tight">
						{t('files.upload.uploadFilesButton')}
					</DialogTitle>
				</DialogHeader>
				<DialogDescription>{t('files.upload.confirmDescription')}</DialogDescription>

				<div className="space-y-4 py-4">
					{/* File List Section */}
					<div>
						<div className="flex justify-between items-center mb-2">
							<p className="text-sm text-foreground">{t('files.upload.filesSelectedCount', { count: files.length })}</p>

							<Button
								variant="ghost"
								size="sm"
								className="h-6 text-xs text-blue-600 hover:text-blue-700 p-0"
								onClick={handleUploadClick}
								disabled={isUploading}
							>
								{t('files.upload.addMore')}
							</Button>
						</div>

						<div className="bg-card border rounded p-2 max-h-48 overflow-y-auto space-y-1">
							{files.map((file, index) => (
								<div
									key={`${file.name}-${index}`}
									className="flex items-center justify-between p-2 hover:bg-muted rounded group transition-colors"
								>
									<div className="flex items-center gap-2 overflow-hidden">
										<FileIcon className="size-4 shrink-0" />
										<div className="flex flex-col overflow-hidden">
											<span className="text-sm truncate font-medium">{file.name}</span>
											<span className="text-[10px] text-muted-foreground">
												{(file.size / (1024 * 1024)).toFixed(2)} MB
											</span>
										</div>
									</div>
									<Button
										variant="ghost"
										size="icon"
										className="size-6 text-muted-foreground hover:text-red-500 hover:bg-transparent p-0"
										onClick={() => removeFile(index)}
										disabled={isUploading}
									>
										<X className="size-4" />
									</Button>
								</div>
							))}
							{files.length === 0 && (
								<div className="text-center py-4 text-zinc-400 text-sm italic">{t('files.upload.noFilesInQueue')}</div>
							)}
						</div>
					</div>

					{/* Asset Structure Section */}
					<div>
						<Label className="text-sm font-medium mb-2 block">
							{t('files.upload.assetStructureLabel')} <span className="text-red-500">*</span>
						</Label>
						<div className="space-y-2">
							<Button
								type="button"
								variant="outline"
								className="bg-input rounded p-3 py-5 cursor-pointer transition-colors w-full justify-start"
								onClick={() => setIsAssetTreeOpen(!isAssetTreeOpen)}
							>
								<div className="flex items-center justify-between w-full">
									<div className="flex items-center gap-2">
										<Building2Icon className="size-4" />
										<span className="text-sm text-wrap">
											{selectedAssetStructureId
												? getAssetBreadcrumbPath(selectedAssetStructureId)
												: t('files.upload.selectAssetPlaceholder')}
										</span>
									</div>
									<ChevronRightIcon className={cn('size-4 transition-transform', isAssetTreeOpen && 'rotate-90')} />
								</div>
							</Button>

							{isAssetTreeOpen && (
								<SubAssetTreeSelector
									selectedId={selectedAssetStructureId}
									onSelect={(id: string) => {
										setSelectedAssetStructureId(id);
										setIsAssetTreeOpen(false);
									}}
									subAssets={subAssetsWithFirstParent}
								/>
							)}
						</div>
					</div>
				</div>

				<DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-2 sm:gap-0">
					<DialogClose asChild>
						<Button variant="outline" className="h-10 px-4 py-2" disabled={isUploading}>
							{t('common.actions.cancel')}
						</Button>
					</DialogClose>

					<Button
						className="h-10 px-4 py-2"
						onClick={onUploadConfirmClick}
						disabled={isUploadBusy || files.length === 0}
					>
						{isUploading ? (
							<>
								<Loader2 className="h-4 w-4 mr-2 animate-spin" />
								{t('common.states.uploading')}
							</>
						) : (
							<>
								<Upload className="h-4 w-4 mr-2" />
								{t('common.actions.upload')} {files.length > 0 && `(${files.length})`}
							</>
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
